import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

const AboutPage = () => {
  return (
    <div className="min-h-screen py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            About Our <span className="gradient-text">MBA Simulator</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Understanding how we evaluate your MBA application chances
          </p>
        </div>

        <div className="card-elevated p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
          <p className="text-gray-700 text-lg leading-relaxed">
            We believe that MBA admissions shouldn't be a mystery. Our simulator provides transparent, 
            AI-powered insights that help you understand your chances at top business schools based on 
            each school's unique culture, values, and evaluation criteria.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="card p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Holistic Evaluation</h3>
            <p className="text-gray-600">
              Our system mirrors real MBA admissions by considering your entire profile, not just test scores. 
              We understand that unique backgrounds can outweigh traditional metrics.
            </p>
          </div>
          
          <div className="card p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">School-Specific Insights</h3>
            <p className="text-gray-600">
              Each evaluation is tailored to the specific school you're applying to, considering their 
              culture, values, and what they look for in ideal candidates.
            </p>
          </div>
        </div>

        <div className="card-elevated p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">How It Works</h2>
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="step-indicator step-current w-8 h-8 flex-shrink-0 mt-1">1</div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Demographics & Academics</h3>
                <p className="text-gray-600">
                  We analyze your background, nationality, test scores, and academic history against 
                  school-specific benchmarks and diversity requirements.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="step-indicator step-current w-8 h-8 flex-shrink-0 mt-1">2</div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Work Experience Analysis</h3>
                <p className="text-gray-600">
                  Your professional experience is evaluated for leadership potential, career progression, 
                  and alignment with the school's culture and values.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="step-indicator step-current w-8 h-8 flex-shrink-0 mt-1">3</div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Essays & Goals Assessment</h3>
                <p className="text-gray-600">
                  Your essays and career goals are analyzed for authenticity, school fit, and 
                  potential contribution to the MBA program.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card-elevated p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Trust Our Evaluation?</h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-gray-700">
                <strong>Research-Based:</strong> Our evaluation criteria are based on extensive research 
                of school websites, admissions blogs, and forum discussions.
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-gray-700">
                <strong>AI-Powered:</strong> We use advanced AI models that balance cost and reasoning 
                ability to provide nuanced evaluations.
              </p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-gray-700">
                <strong>Continuously Updated:</strong> Our school profiles and evaluation criteria are 
                regularly updated based on the latest admissions information.
              </p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Get Started?</h2>
          <p className="text-gray-600 mb-8">
            Get your personalized MBA admissions evaluation in just a few minutes.
          </p>
          <Link
            to="/simulator"
            className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors duration-200"
          >
            Start Your Evaluation
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AboutPage; 